import type { TenantName } from '@root/config/types';

import type {
  BrandingAJG,
  BrandingArtificial,
  BrandingBMS,
  BrandingHowden,
  BrandingLockton,
  BrandingMcGill,
} from './types';

const WITH_COVERS_SUFFIX = ' (with covers)';

type PdfBrandingByClient = {
  artificial: BrandingArtificial;
  'ct-ajg': BrandingAJG;
  'ct-bms': BrandingBMS;
  'ct-howden': BrandingHowden;
  'ct-lockton': BrandingLockton;
  'ct-mcgill': BrandingMcGill;
};

// The single list of base brandings that offer a "(with covers)" variant. The jacket registry in
// backend/services/pdf/branding/jackets/ is typed from it, so a brand added here without a jacket
// (or a jacket without an entry here) fails the type check.
export const BRANDINGS_WITH_COVERS = {
  artificial: ['Artificial', 'Artificial Brand 2'],
} as const satisfies { [C in keyof PdfBrandingByClient]?: readonly PdfBrandingByClient[C][] };

export type ClientWithCovers = keyof typeof BRANDINGS_WITH_COVERS;
export type BrandingWithCoversOf<C extends ClientWithCovers> = (typeof BRANDINGS_WITH_COVERS)[C][number];

export const getBrandingsWithCovers = (client?: TenantName): readonly string[] =>
  client && Object.hasOwn(BRANDINGS_WITH_COVERS, client) ? BRANDINGS_WITH_COVERS[client as ClientWithCovers] : [];

export const withCovers = (branding: string): string => `${branding}${WITH_COVERS_SUFFIX}`;

export const isBrandingWithCovers = (branding?: string): boolean => !!branding?.endsWith(WITH_COVERS_SUFFIX);

// The "(with covers)" variant renders the base branding's header, footer and margins.
export const getBaseBranding = (branding?: string): string | undefined =>
  isBrandingWithCovers(branding) ? branding?.slice(0, -WITH_COVERS_SUFFIX.length) : branding;
